import React from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import AppLink from "./AppLink";

/**
 * Bilgi kartı — başlıkta renkli ikon + ad, altında etiket/değer satırları.
 * Cihaz detayındaki "Firma Bilgileri" kartının deseni; kullanıcı detayındaki
 * tüm bilgi grupları bununla çizilir.
 *
 * `linkTo` verilirse kartın tamamı bağlantıdır (sağ üstte dış bağlantı ikonu).
 * `action` başlığın sağ üstüne konur (ör. aktiflik anahtarı) — ikisi birlikte
 * kullanılmaz. Kart `h-full`: aynı satırdaki kartlar eşit yükseklikte durur.
 *
 * Kullanım:
 *   <InfoCard icon={FaUser} iconBg="bg-indigo-500" title="Kişisel Bilgiler">
 *     <InfoCard.Row label="İsim">Taha</InfoCard.Row>
 *   </InfoCard>
 */
function InfoCard({
  icon: Icon,
  iconBg = "bg-blue-500",
  title,
  dirty = false,
  action,
  linkTo,
  linkState,
  linkLabel,
  children,
}) {
  return (
    <div
      className={`relative h-full rounded-xl bg-white border shadow-sm sm:hover:shadow-md transition-all overflow-hidden ${
        dirty ? "border-blue-300" : "border-gray-200"
      } ${linkTo ? "cursor-pointer hover:border-blue-300 group/card" : ""}`}
    >
      {linkTo && (
        <AppLink
          to={linkTo}
          state={linkState}
          className="absolute inset-0 z-0"
          aria-label={linkLabel || title}
        />
      )}
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 min-h-[52px] bg-gray-50/80 border-b border-gray-100">
        <div className="flex items-center gap-2.5 min-w-0">
          {Icon && (
            <div className={`${iconBg} text-white p-1.5 rounded-lg shadow-sm shrink-0`}>
              <Icon className="w-4 h-4" />
            </div>
          )}
          <span className="text-sm font-SemiBold text-gray-800 truncate">{title}</span>
          {dirty && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />}
        </div>
        {action && <div className="relative z-10 shrink-0">{action}</div>}
        {!action && linkTo && (
          <FaExternalLinkAlt className="w-3 h-3 text-gray-300 group-hover/card:text-blue-500 transition-colors shrink-0" />
        )}
      </div>
      <div className="px-4 py-1">{children}</div>
    </div>
  );
}

/** Etiket solda, değer sağda; son satırda alt çizgi yok. */
function InfoCardRow({ label, dirty = false, children }) {
  return (
    <div className="flex items-center justify-between min-h-[44px] py-2 border-b border-gray-100 last:border-b-0 gap-3">
      <span className="flex items-center gap-1.5 text-xs font-Medium text-gray-500 shrink-0">
        {label}
        {dirty && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
      </span>
      <div className="min-w-0 flex justify-end text-right">{children}</div>
    </div>
  );
}

InfoCard.Row = InfoCardRow;

export default InfoCard;
